import { optionSchema } from '@/common/schemas/option.schema'
import { isValidDateString, isValidTimeString } from '@/common/utils/dateValidator'
import { unFormatUnknownUnit } from '@/common/utils/unFormatters'
import { MaintenanceForwardingsEstablishmentChoiceEntryStepOptions } from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/constants/maintenanceForwardingsShowDirectiveDetails.type'
import z from 'zod'

export const directiveComplexitySchema = z.object({
  complexity: optionSchema,
})

export const directiveDiagnosticSchema = z.object({
  diagnostic: optionSchema,
})

export const establishmentChoiceEntrySchema = z.discriminatedUnion('establishment_choice_entry_type', [
  z.object({
    establishment_choice_entry_type: z.literal(
      MaintenanceForwardingsEstablishmentChoiceEntryStepOptions.ManualEntry
    ),
    establishment_choice_entry_attributes: z.object({
      establishment_id: z.string(),
    }),
  }),
  z.object({
    establishment_choice_entry_type: z.literal(
      MaintenanceForwardingsEstablishmentChoiceEntryStepOptions.AuctionEntry
    ),
    establishment_choice_entry_attributes: z
      .object({
        will_select_allowed_establishments: z.string(),
        minimum_of_proposals_to_finish: z.string().refine(unFormatUnknownUnit),
        allowance_radius_in_kilometers: z.string().optional(),
        finishes_at_date: z.string().refine(isValidDateString),
        finishes_at_time: z.string().refine(isValidTimeString),
        wip_schedule: z.boolean().optional(),
        starts_at_date: z.string().optional(),
        starts_at_time: z.string().optional(),
        allowed_establishment_ids: z.array(z.string()).optional(),
        kind: optionSchema,
      })
      .superRefine((data, ctx) => {
        if (data.wip_schedule) {
          if (!isValidDateString(data.starts_at_date ?? '')) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Data inválida', path: ['starts_at_date'] })
          }
          if (!isValidTimeString(data.starts_at_time ?? '')) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Horário inválido', path: ['starts_at_time'] })
          }
        }

        if (data.will_select_allowed_establishments === '0' && (!data.allowance_radius_in_kilometers || !unFormatUnknownUnit(data.allowance_radius_in_kilometers))) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Campo obrigatório', path: ['allowance_radius_in_kilometers'] })
        }
      }),
  }),
])

export const checkinEntrySchema = z.object({
  checkin_entry_type: z.string(),
  checkin_entry_attributes: z.object({
    responsible: optionSchema,
  }),
})

export type DirectiveComplexityValidationSchema = z.infer<typeof directiveComplexitySchema>

export type DirectiveDiagnosticValidationSchema = z.infer<typeof directiveDiagnosticSchema>

export type EstablishmentChoiceEntryValidationSchema = z.infer<typeof establishmentChoiceEntrySchema>

export type EstablishmentChoiceEntryManualEntrySchema = Extract<
  EstablishmentChoiceEntryValidationSchema,
  { establishment_choice_entry_type: MaintenanceForwardingsEstablishmentChoiceEntryStepOptions.ManualEntry }
>

export type EstablishmentChoiceEntryAuctionEntrySchema = Extract<
  EstablishmentChoiceEntryValidationSchema,
  { establishment_choice_entry_type: MaintenanceForwardingsEstablishmentChoiceEntryStepOptions.AuctionEntry }
>

export type CheckinEntryValidationSchema = z.infer<typeof checkinEntrySchema>
